/**
 * FNV-1a, 32-bit — the world hash (TRD-00 R-00-08, TRD-01 R-01-13).
 *
 * The hash is not a checksum for storage integrity; it is a *divergence detector*.
 * Two runs with the same seed and command log must produce identical hashes at every
 * tick, and when they don't, the first divergent tick is the bug.
 *
 * Order-dependence is a feature: entity iteration order is part of the determinism
 * contract (TRD-00 R-00-05), so a reordering must change the hash.
 */

export const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

export class Hasher {
  private h: number;

  /** Pass a previous hash to continue a rolling digest across ticks (see World.runHash). */
  constructor(seed: number = FNV_OFFSET_BASIS) {
    this.h = seed >>> 0;
  }

  /** Mix in a 32-bit integer, byte by byte. */
  u32(value: number): this {
    let v = value >>> 0;
    for (let i = 0; i < 4; i++) {
      this.h = Math.imul(this.h ^ (v & 0xff), FNV_PRIME) >>> 0;
      v >>>= 8;
    }
    return this;
  }

  /** Mix in a signed integer. */
  int(value: number): this {
    return this.u32(value | 0);
  }

  /**
   * Mix in a float via fixed-point quantization.
   *
   * Quantizing to 1/4096 of a tile keeps the hash from being hypersensitive to
   * last-bit noise that no player could ever observe, while still catching any
   * divergence large enough to change gameplay (TRD-01 R-01-13).
   */
  float(value: number): this {
    return this.int(Math.round(value * 4096));
  }

  bool(value: boolean): this {
    return this.u32(value ? 1 : 0);
  }

  /** Mix in a bigint (RNG stream position) as two 32-bit halves. */
  bigint(value: bigint): this {
    const v = value & 0xffffffffffffffffn;
    this.u32(Number(v & 0xffffffffn));
    this.u32(Number((v >> 32n) & 0xffffffffn));
    return this;
  }

  /** Mix in a short string, e.g. a phase or an id. */
  str(value: string): this {
    for (let i = 0; i < value.length; i++) {
      this.h = Math.imul(this.h ^ value.charCodeAt(i), FNV_PRIME) >>> 0;
    }
    return this;
  }

  get value(): number {
    return this.h >>> 0;
  }
}
